import React, { useEffect } from 'react';
import { useController, ApplicationView as view } from '@aha-app/mvc';
import TemplateController, { TemplateCategories } from './TemplateController';
import styles from './PageTemplateModal.mod.less';
import classnames from 'classnames';
import AhaAnalytics from 'javascripts/analytics';
import DesktopNotesController from 'components/create_homepage/controllers/DesktopNotesController';
import defaultScreenshot from 'images/pages/templates/missing_screenshot.png';
import { humanize } from 'inflected';
import { type GlobalNoteTemplate } from './types';
import { type NoteTemplate } from '../framework/models';

interface DetailTemplateModalProps {
  template: GlobalNoteTemplate | NoteTemplate;
}

const DetailTemplateModal: React.FC<DetailTemplateModalProps> = ({
  template,
}) => {
  const notesController = useController(DesktopNotesController);
  const controller = useController(TemplateController);

  // Set all anchor elements to open in a new page in the best practices section
  useEffect(() => {
    const bestPracticesDiv = document.getElementById('best-practices');
    if (bestPracticesDiv) {
      const anchors = bestPracticesDiv.getElementsByTagName('a');
      for (let i = 0; i < anchors.length; i++) {
        const anchor = anchors[i];
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noopener noreferrer');
      }
    }
  }, []);

  const canCreatePages = notesController
    ? notesController.state.canCreatePages
    : true;
  const { isGlobal, name, shortDescription, longDescription } = template;
  const categories = template.categories ?? [];

  const templateLink = controller.linkForTemplate(template);

  const templateButton = (
    <aha-button
      style={{
        width: '188px',
        cursor: 'pointer',
        marginRight: '23px',
        fontSize: '17px',
      }}
      size='large'
      onClick={event => {
        if (!canCreatePages) return;

        event.stopPropagation();
        event.preventDefault();

        controller.actionPlaceTemplate(template);
        AhaAnalytics.track(
          'Feature-7013353833234815065',
          'User selects template',
          {
            'Template type': humanize(template.documentType),
            Location: 'Template detail page',
            'Template category': categories.join(', '),
            'Template name': template.name,
            'CTA used': 'Use Template',
            'Template origin': controller.isGlobalTemplate(template)
              ? 'Aha! Admin'
              : categories.includes(TemplateCategories.Personal)
              ? 'Personal'
              : 'Workspace',
          }
        );
      }}
      kind='primary'
      disabled={canCreatePages ? null : true}
    >
      Use template
    </aha-button>
  );

  const buttonContent = templateLink ? (
    <a
      href={templateLink}
      onClick={event => {
        event.stopPropagation();
        event.preventDefault();
      }}
    >
      {templateButton}
    </a>
  ) : (
    templateButton
  );

  return (
    <div className={styles.previewModal}>
      <div
        className={styles.previewHeader}
        style={{ height: isGlobal === false ? 'auto' : undefined }}
      >
        {isGlobal && (
          <div
            className={styles.previewHeaderButton}
            onClick={() => controller.actionSetCurrentTemplate(undefined)}
          >
            <aha-icon icon='far fa-left-long' style={{ marginRight: '5px' }} />
            Back to template gallery
          </div>
        )}
        <aha-icon
          class={styles.closeButton}
          icon='far fa-close'
          onClick={() => controller.actionAbortChooseTemplate()}
        />
      </div>
      <div className={styles.previewContent}>
        <div className={styles.previewDetail}>
          <div className={styles.detailLeft}>
            <div className={styles.templateCategory}>
              {isGlobal ? categories.join(', ') : 'Custom template'}
            </div>
            <div className={styles.templateName}>{name}</div>
            {isGlobal ? (
              <div
                className={styles.templateCaption}
                // variable name indicates HTML; setter is responsbile for safety
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: shortDescription?.htmlBody ?? '',
                }}
              />
            ) : (
              <div className={styles.templateCaption}>
                Create a new document using the “{template.name}” template
                {window.currentProject
                  ? ` in the ${window.currentProject.name} workspace`
                  : ''}
                .
              </div>
            )}
            <div>{buttonContent}</div>
          </div>
          <div className={classnames(styles.detailRight)}>
            <img
              src={
                template.screenshot ||
                (!controller.isGlobalTemplate(template) &&
                  template.thumbnailUrl) ||
                defaultScreenshot
              }
            />
          </div>
        </div>
        {isGlobal && (
          <div className={styles.previewHowTo}>
            <div
              id='best-practices'
              className={classnames('aha-editor-field', styles.bestPractices)}
              // variable name indicates HTML; setter is responsbile for safety
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: longDescription?.htmlBody ?? '',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default view(DetailTemplateModal);
